import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { selectors, useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography } from '@altermaind/web/ui';
import { AlertBadge, RenderLocalesWithComponents } from '@/_shared/components';
import { environment } from '@/_shared/configuration/env';
import { Routes } from '@/_shared/constants/routes';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { SendSurvey } from '@/surveys/components/SendSurvey';

export const EmptyState: FC = () => {
  const t = useTranslations('departments.tabs.assessment.noData');

  const params = useParams();

  const [, setTrigger] = useState(false);

  const {
    isOpen: isOpenSendSurvey,
    closeDialog: closeDialogSendSurvey,
    openDialog: openDialogSendSurvey,
  } = useDialog('sendSurvey');

  const { genericResponsiveValue } = useResponsiveStyle();

  const currentLanguage = useSelector(selectors.selectCurrentLanguage);

  const goToPublicsDocs = useCallback(() => {
    const documentationUrl = `${environment.VITE_APP_PUBLIC_DOCS}/${currentLanguage === 'it' ? '' : currentLanguage}`;
    window.open(documentationUrl, '_blank');
  }, [currentLanguage]);

  const CustomRedirectOnClose = useMemo(() => {
    if (params.teamId && params.departmentId) {
      return Routes.DepartmentsTeamsDetail.replace(':departmentId', params.departmentId).replace(
        ':teamId',
        params.teamId,
      );
    }
    if (params.departmentId) {
      return Routes.DepartmentsDetail.replace(':departmentId', params.departmentId);
    }
    return undefined;
  }, [params.departmentId, params.teamId]);

  return (
    <Box
      maxWidth={genericResponsiveValue({
        defaultValue: '80%',
        breakPoints: { mobile: '100%' },
      })}
      mx='auto'
      textAlign='center'
      mt='xl'
    >
      <Typography variant='h2' mb='xs'>
        {t('title')}
      </Typography>
      <Typography variant='p3/light' mb='m'>
        {t('description')}
      </Typography>
      <Button onClick={openDialogSendSurvey} mx='auto' variant='primary'>
        {t('button.label')}
      </Button>
      <Box
        maxWidth={genericResponsiveValue({
          defaultValue: '80%',
          breakPoints: { mobile: '100%' },
        })}
        mx='auto'
      >
        <AlertBadge
          containerProps={{ mt: 'l' }}
          type='transparent'
          iconName='lightBulb'
          label={
            <Typography variant='p2/light'>
              <RenderLocalesWithComponents
                text={t('alert.label', {
                  downloadFile: '!{CTA}!',
                })}
                placeholders={{
                  '!{CTA}!': (
                    <Typography
                      as='span'
                      data-testid='go-to-public-docs'
                      color='primary'
                      fontWeight='500'
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={goToPublicsDocs}
                    >
                      {t('alert.ctaLabel')}
                    </Typography>
                  ),
                }}
              />
            </Typography>
          }
        />
      </Box>
      <SendSurvey
        setTrigger={setTrigger}
        preSelection
        isOpen={isOpenSendSurvey}
        closeDialog={closeDialogSendSurvey}
        customRedirectOnClose={CustomRedirectOnClose}
      />
    </Box>
  );
};
